import React from 'react';
import {useTranslation} from 'react-i18next';
import {useHistory, useLocation} from 'react-router-dom';
import {FormProvider, useForm} from 'react-hook-form';
import cn from 'classnames';
import {get} from 'lodash';
import {PATHS} from 'Routes';
import {useComputedReservationDetails} from 'context/computedReservationDetails';
import {useRegisterSteps} from '../../hooks';
import {useIdentityVerification} from '../VerifyModalScreen/useIdentityVerification';
import {DocumentPhoto, Side} from 'views/EditRegistredGuest/EditRegistredGuest';
import {ResultLocationStateType} from 'views/ConfirmIdentityResultScreen/ConfirmIdentityResultScreen';
import {getFields} from '../../components/AddPersonalDataForm/AddPersonalDataForm';
import {FORM_NAMES, FormTypes} from '../../components/AddPersonalDataForm';
import {getDocTypes} from '../../utils/docTypes';
import {SelectOptionType} from '../../utils/types';
import {VerifyLocationStateType} from '../VerifyModalScreen/VerifyModalScreen';
import Page from 'components/acommon/Page';
import CheckinNavigation from 'components/acommon/CheckinNavigation';
import Link from 'components/acommon/Link';
import Button from 'components/acommon/Button';
import styles from './ConfirmIdentityGuideScreen.module.sass';
import {
  IdentityForm,
  IdentityTypeSelector,
  IdentityTypeSelectorItem,
} from 'components/acommon/IdentityItems';
import useMixPanel from 'hooks/useMixPanel';

export const getPersistedState = (location: {
  state: RetakeScreenLocationStateType;
}): RetakeScreenLocationStateType => {
  return {
    ...location.state,
    guestId: '',
  };
};

type RetakeScreenLocationStateType = ResultLocationStateType &
  VerifyLocationStateType & {
    isVerifying: boolean;
    formData: Record<string, unknown>;
    fromHousingFlow?: boolean;
    guestId?: string;
    isRetryBiomatch?: boolean;
    documentType?: string;
    identityType?: string;
  };

function Retake() {
  const history = useHistory();
  const location = useLocation<RetakeScreenLocationStateType>();
  const {t} = useTranslation();
  const reservationDetails = useComputedReservationDetails();
  const {count, getCurrentStep} = useRegisterSteps();

  const documentType = get(location, 'state.documentType.value');
  const nationality = get(location, 'state.formData.nationality.value');
  const {isDocTwoSides} = useIdentityVerification({
    nationality,
    documentType,
  });

  // const members = get(reservationDetails.reservation, 'guest_group.members');
  // const result = find(members, {id: location.state.guestId});
  // const {refreshReservation} = useReservation();
  //
  // const updateGuestQuery = () => {
  //   const state = getPersistedState(location);
  //   const reservationId = reservationDetails.reservation.id;
  //   if (state?.guestId) {
  //     return api.guests.patch(state?.guestId, {
  //       ...state.biomatch,
  //       reservation_id: reservationId,
  //     });
  //   }
  // };

  // const {mutate: updateGuestMutation} = useSWR(
  //   getURL(api.guests.ENDPOINTS.all(location.state?.guestId || '')),
  //   updateGuestQuery,
  // );

  const goNext = () => {
    const state = getPersistedState(location);

    // if (state.isRetryBiomatch) {
    //   updateGuestMutation();
    //   refreshReservation();
    //   return history.push(PATHS.formPersonalData, state);
    // }

    history.push(PATHS.formPersonalData, state);
  };

  return (
    <div className={cn(styles.container)}>
      <div className={cn(styles.retakeContainer)}>
        <DocumentPhoto
          photo={location.state.frontSideDocumentPhoto?.picture}
          side={Side.front}
          connectedStatus={location.state.frontSideDocumentPhoto?.status!}
          guestId={location.state.guestId}
        />

        {isDocTwoSides && (
          <DocumentPhoto
            photo={location.state.backSideDocumentPhoto?.picture}
            side={Side.back}
            connectedStatus={location.state.backSideDocumentPhoto?.status!}
            guestId={location.state.guestId}
          />
        )}

        {reservationDetails.hasBiomatch && (
          <DocumentPhoto
            photo={location.state.selfiePhoto?.picture}
            side={Side.selfie}
            connectedStatus={location.state.selfiePhoto?.result?.status}
            guestId={location.state.guestId}
          />
        )}
      </div>
      <div className={styles.buttonsWrapperMobile}>
        <div className={styles.progress}>{`${getCurrentStep(
          PATHS.verificationResult,
        )}/${count}`}</div>
        <Button rightIcon="arrow-next" className={styles.statusButton} onClick={goNext}>
          {t('next')}
        </Button>
      </div>
    </div>
  );
}

export enum IdentityVerificationMethod {
  webcam = 'webcam',
  file = 'file',
}

export type DocType = {
  label: string;
  value: string;
  onClick: () => void;
};

export const getIdentityTypeItems = (
  onClick: (methodType: any) => void,
  translate: (text: string) => string,
  options?: {isUploadDisabled?: boolean},
): IdentityTypeSelectorItem[] => [
  {
    label: translate('take_picture_with_your_webcam'),
    onClick: () => onClick(IdentityVerificationMethod.webcam),
    value: IdentityVerificationMethod.webcam,
  },
  {
    label: translate('upload_file'),
    onClick: () => onClick(IdentityVerificationMethod.file),
    value: IdentityVerificationMethod.file,
    isDisabled: options?.isUploadDisabled,
  },
];

function ConfirmIdentityGuideScreen() {
  const {t, i18n} = useTranslation();
  const history = useHistory();
  const location = useLocation<RetakeScreenLocationStateType>();
  const {
    hasBiomatchOptionalCurrentGuest,
    hasScanDocumentOptionalCurrentGuest,
    isVerifyOnlyDocument,
    isReservationLoaded,
    reservation,
    isPoliceActivated,
    hasGuestMembers,
    hasAnyMandatoryPayment,
    paymentsDetails,
  } = useComputedReservationDetails();
  const [step, setStep] = React.useState(0);
  const [documentType, setDocumentType] = React.useState<SelectOptionType>(
    location.state?.documentType,
  );
  const [identityType, setIdentityType] = React.useState<IdentityVerificationMethod>(
    location.state?.identityType,
  );
  const [documentTypeOptions, setDocumentTypeOptions] = React.useState<DocType[]>();
  const methods = useForm<FormTypes>();
  const {watch, handleSubmit, getValues, reset, formState} = methods;
  const nationality = watch(FORM_NAMES.nationality);
  const residenceCountry = watch(FORM_NAMES.residenceCountry);
  const citizenship = watch(FORM_NAMES.citizenship);
  const formData = getValues();
  const {IdVerificationStart} = useMixPanel();

  const isNotFilledFields = React.useMemo(() => {
    if (!Object.values(formData).length) return true;
    return Object.values(formData).some(field => !field);
  }, [formData]);

  const isVerifyOptional =
    hasBiomatchOptionalCurrentGuest || hasScanDocumentOptionalCurrentGuest;

  const getDisplayFields = React.useCallback(() => {
    if (!isReservationLoaded) return {display: {}};
    return getFields({
      reservation,
      isSaltoLock: false,
      docType: '',
      residenceCountry: '',
      isPoliceActivated: isPoliceActivated,
      nationalityCode: nationality?.value,
      isCustomForm: false,
    });
  }, [isPoliceActivated, isReservationLoaded, nationality?.value, reservation]);

  const [fields, setFields] = React.useState(getDisplayFields);

  React.useEffect(() => {
    if (!isNotFilledFields) {
      setStep(1);
    }
    if (identityType) {
      setStep(2);
    }
  }, [identityType, isNotFilledFields]);

  React.useEffect(() => {
    setFields(getDisplayFields);
  }, [getDisplayFields]);

  React.useLayoutEffect(
    function redirectIfPaymentsRequired() {
      const hasRequiredPayment = hasGuestMembers && hasAnyMandatoryPayment;
      if (hasRequiredPayment) {
        const redirectPathname =
          !paymentsDetails?.deposit.paid && paymentsDetails?.deposit.required
            ? PATHS.protect
            : PATHS.pay;
        history.push(redirectPathname, location.state);
      }
    },
    [
      hasAnyMandatoryPayment,
      paymentsDetails?.deposit,
      hasGuestMembers,
      location.state,
      history,
    ],
  );

  React.useEffect(
    function preloadFormValues() {
      const storedFormData = location.state?.formData;
      if (!storedFormData || formState.isDirty) return;
      const formData = Object.entries(fields.display).reduce<Record<string, unknown>>(
        (acc, [fieldName, display]) => {
          if (display && storedFormData[fieldName]) {
            acc[fieldName] = storedFormData[fieldName];
          }
          return acc;
        },
        {},
      );

      reset(formData);
    },
    [fields.display, formState.isDirty, location.state?.formData, reset],
  );

  // React.useEffect(() => {
  //   if (location.state?.isVerifiedOrRetake) {
  //     localStorage.setItem('photosState', JSON.stringify(location.state));
  //   }
  // }, [location.state]);

  const handleSetIdentityType = (type: IdentityVerificationMethod) => {
    setIdentityType(type);
  };

  const identityTypeItems = getIdentityTypeItems(handleSetIdentityType, t);

  const getDocumentTypeOptions = React.useCallback(() => {
    const docTypes = getDocTypes(reservation, nationality?.value, {
      residenceCountry: residenceCountry?.value,
      citizenship: citizenship?.value,
    });

    return docTypes.map(doc => ({
      label: doc.label,
      value: doc.value,
      onClick: () => setDocumentType(doc),
    }));
  }, [citizenship, nationality, reservation, residenceCountry]);

  React.useEffect(() => {
    const nextDocTypeItems = getDocumentTypeOptions();
    setDocumentTypeOptions(nextDocTypeItems);
  }, [getDocumentTypeOptions, i18n.language]);

  const onSubmit = (data: FormTypes) => {
    IdVerificationStart();
    const formPayload = {
      [FORM_NAMES.nationality]: data?.[FORM_NAMES.nationality],
      [FORM_NAMES.citizenship]: data?.[FORM_NAMES.citizenship],
      [FORM_NAMES.residenceCountry]: data?.[FORM_NAMES.residenceCountry],
      [FORM_NAMES.docType]: documentType,
    };

    history.push(PATHS.verificationDocumentFront, {
      ...location.state,
      formData: {...location.state?.formData, ...formPayload},
      documentType,
      identityType,
    });
  };

  return (
    <Page showFooterOnlyMobile>
      <CheckinNavigation title={t('verify_your_identity')} />
      {location.state?.isVerifiedOrRetake ? (
        <Retake />
      ) : (
        <>
          <div className={styles.description}>
            <div className={styles.warning}>
              {isVerifyOnlyDocument
                ? t('confirm_identity_documents_document_only_description')
                : t('confirm_identity_documents_description')}
            </div>
          </div>
          <div className={cn(styles.container, styles.row)}>
            <FormProvider {...methods}>
              <div className={styles.column}>
                <IdentityForm
                  title={t('select_nationality')}
                  displayFields={fields.display}
                />
                {isVerifyOptional && (
                  <Link
                    to={{pathname: PATHS.formPersonalData, state: location.state}}
                    className={styles.skipLink}
                    densed
                  >
                    {t('dont_verify_my_identity')}
                  </Link>
                )}
              </div>
              {step >= 1 && (
                <div className={styles.column}>
                  <IdentityTypeSelector
                    title={t('type_verify_selector_title')}
                    items={identityTypeItems}
                    value={identityType}
                  />
                </div>
              )}
              {step >= 2 && (
                <div className={styles.column}>
                  {identityType && (
                    <IdentityTypeSelector
                      title={t('identity_type_selector_title')}
                      items={documentTypeOptions}
                      value={documentType}
                      onAction={handleSubmit(onSubmit)}
                      actionLabel={t('next')}
                    />
                  )}
                </div>
              )}
            </FormProvider>
          </div>
        </>
      )}
    </Page>
  );
}

export {ConfirmIdentityGuideScreen};
